import { Trans, useLingui } from '@lingui/react/macro';
import classNames from 'classnames';
import { format } from 'date-fns';
import _ from 'lodash';
import { useMainLayout } from 'modules/app-shell/ui/layout/context';
import { getSportTypeLabel } from 'modules/core/domain/constants/sport-type-labels';
import { SportType } from 'modules/core/domain/enums/sport-type';
import { Pages } from 'modules/navigation/domain/enums/pages';
import { OrganizerListItem } from 'modules/organizers/domain/interfaces/organizer-list-item';
import { formatSubscriptionExpiry } from 'modules/organizers/domain/utils/format-subscription-expiry';
import { useOrganizersList } from 'modules/organizers/ui/hooks/use-organizers-list';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { SingleValue } from 'react-select';
import { USER_DEFAULT_AVATAR } from 'shared/constants/links';
import UDInput from 'ud-form/components/input/component';
import { SelectOption, UDSelect } from 'ud-form/components/select/component';
import UDHeaderTitle from 'ud-ui/components/header-title';
import Label from 'ud-ui/components/label';
import { UDEntityRow, UDPagedList } from 'ud-ui/components/paged-list';
import UDText from 'ud-ui/components/text';

/**
 * Колонка правого блока: приглушённый ярлык сверху, значение снизу.
 * Ширина фиксированная — так колонки выстраиваются в столбцы и не слипаются
 * между собой, когда значения разной длины.
 */
function OrganizerFact({ label, value, className }: { label: string; value: string; className?: string }) {
  return (
    <div className={classNames('flex flex-col gap-0.5', className)}>
      <UDText type="caption-2" className="text-surfaceRomanSilver30">
        {label}
      </UDText>
      <UDText type="subhead" className="truncate">
        {value}
      </UDText>
    </div>
  );
}

function OrganizersList() {
  const navigate = useNavigate();
  const { t } = useLingui();
  const { setHeaderContent } = useMainLayout();

  const {
    items,
    itemsOffset,
    state,
    page,
    totalPages,
    perPage,
    draftSearch,
    setPage,
    setPerPage,
    setSearch,
    buildPageHref,
    filters,
    filterDrafts,
    setFilter,
  } = useOrganizersList();

  useEffect(() => {
    setHeaderContent(
      <UDHeaderTitle>
        <Trans>Организаторы</Trans>
      </UDHeaderTitle>,
    );
    return () => setHeaderContent(null);
  }, [setHeaderContent]);

  const sportTypeOptions = useMemo<SelectOption<string>[]>(() => {
    const types = _.keys(SportType).map((key) => {
      const label = getSportTypeLabel(key);
      return { value: key, label: _.capitalize(label ? t(label) : key) };
    });
    return [{ value: '', label: t({ message: 'Все', comment: 'Фильтр «Тип спорта»: без ограничения' }) }, ...types];
  }, [t]);

  const sortOptions = useMemo<SelectOption<string>[]>(
    () => [
      { value: 'DESC', label: t({ message: 'Новые', comment: 'Сортировка организаторов: сначала новые' }) },
      { value: 'ASC', label: t({ message: 'Старые', comment: 'Сортировка организаторов: сначала старые' }) },
      { value: 'SUBSCRIPTION_EXPIRING', label: t`Подписка истекает` },
    ],
    [t],
  );

  const selectedSportType = sportTypeOptions.find((option) => option.value === filters.sportType) ?? sportTypeOptions[0];
  const selectedSort = sortOptions.find((option) => option.value === filters.sort) ?? sortOptions[0];

  const handleSportTypeChange = useCallback(
    (option: SingleValue<SelectOption<string>>) => setFilter('sportType', option?.value ?? ''),
    [setFilter],
  );

  const handleSortChange = useCallback(
    (option: SingleValue<SelectOption<string>>) => setFilter('sort', option?.value ?? 'DESC'),
    [setFilter],
  );

  const extraFilters = useMemo(
    () => (
      <>
        <Label text={t`Телефон:`}>
          <UDInput
            placeholder="79139332245"
            value={filterDrafts.phone}
            onChange={(event) => setFilter('phone', event.target.value)}
            wrapperProps={{ style: { width: 180 } }}
          />
        </Label>

        <Label text={t`Тип спорта:`} style={{ width: 180 }}>
          <UDSelect isMulti={false} options={sportTypeOptions} value={selectedSportType} onChange={handleSportTypeChange} />
        </Label>

        <Label text={t`Сортировка:`} style={{ width: 200 }}>
          <UDSelect isMulti={false} options={sortOptions} value={selectedSort} onChange={handleSortChange} />
        </Label>
      </>
    ),
    [
      filterDrafts.phone,
      handleSortChange,
      handleSportTypeChange,
      selectedSort,
      selectedSportType,
      setFilter,
      sortOptions,
      sportTypeOptions,
      t,
    ],
  );

  // Номер строки продолжается со страницы на страницу и считается от `itemsOffset`,
  // а не от `page`: пока едет следующая страница, на экране ещё прошлые элементы.
  const renderOrganizer = useCallback(
    (organizer: OrganizerListItem, index: number) => {
      const admin = organizer.adminUser;
      const fullName = admin ? [admin.surname, admin.name, admin.middleName].filter(Boolean).join(' ') : undefined;
      const sportTypeLabel = getSportTypeLabel(organizer.sportType);

      return (
        <UDEntityRow
          index={itemsOffset + index + 1}
          avatar={organizer.logoUrl ?? USER_DEFAULT_AVATAR}
          title={organizer.name}
          subtitle={
            <span className="flex gap-4">
              {fullName && <span>{fullName}</span>}
              {organizer.phone && <span>{organizer.phone}</span>}
              {organizer.address?.city && <span>{organizer.address.city}</span>}
            </span>
          }
          actions={
            <div className="flex items-center gap-6">
              <OrganizerFact
                label={t({ message: 'Создан', comment: 'Дата создания организатора' })}
                value={format(new Date(organizer.createdAt), 'dd.MM.yyyy')}
                className="w-24"
              />
              <OrganizerFact label={t`Подписка`} value={formatSubscriptionExpiry(organizer.subscriptionSummary)} className="w-24" />
              <div className="bg-BorderGray50 w-40 rounded-[10px] px-3.5 py-1.75">
                <UDText type="subhead" className="truncate text-center">
                  {organizer.sportType ? _.capitalize(sportTypeLabel ? t(sportTypeLabel) : organizer.sportType) : '-'}
                </UDText>
              </div>
            </div>
          }
          onClick={() => navigate(Pages.ORGANIZERS.builders.view(organizer.id))}
        />
      );
    },
    [itemsOffset, navigate, t],
  );

  return (
    <UDPagedList<OrganizerListItem>
      className="flex-1"
      items={items}
      renderItem={renderOrganizer}
      getItemKey={(organizer) => organizer.id}
      state={state}
      page={page}
      totalPages={totalPages}
      perPage={perPage}
      onPageChange={setPage}
      onPerPageChange={setPerPage}
      pageHref={buildPageHref}
      search={draftSearch}
      onSearchChange={setSearch}
      searchPlaceholder={t`Название (от 3 символов)`}
      extraFilters={extraFilters}
      emptyText={t`Организаторов пока нет`}
      notFoundText={t`По такому запросу ничего не найдено`}
      errorText={t`Ошибка загрузки данных. Попробуйте изменить фильтры или обновить страницу.`}
    />
  );
}

export default React.memo(OrganizersList);
